import type { api } from '@udm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileDown, Trash2, Upload } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  addRelation,
  addSubjectEvent,
  createAsset,
  deleteAsset,
  deleteDocument,
  deleteRelation,
  documentUrl,
  fetchAsset,
  fetchAssets,
  updateAsset,
  uploadDocument,
} from '../../api/ops';
import { getDataSource } from '../../data';
import { Drawer, Field } from '../../ui/components';
import { formatBytes, formatDateTime } from '../../ui/format';

export interface Draft {
  id: string | null;
  client_id: string;
  type_key: string;
  geometryKind: 'point' | 'line' | 'polygon';
  coords: [number, number][];
}

const RELATIONS: api.Relation['kind'][] = [
  'mounted_on',
  'fed_from',
  'part_of',
  'controls',
  'connected_to',
  'located_in',
];
const MAINT: api.SubjectEventCreate['kind'][] = [
  'maintenance',
  'inspection',
  'repair',
  'replacement',
  'note',
];

export function geometryOf(d: Draft): api.GeoJsonGeometry | null {
  if (!d.coords.length) return null;
  if (d.geometryKind === 'point') return { type: 'Point', coordinates: d.coords[0] };
  if (d.geometryKind === 'line')
    return d.coords.length > 1 ? { type: 'LineString', coordinates: d.coords } : null;
  return d.coords.length > 2 ? { type: 'Polygon', coordinates: [[...d.coords, d.coords[0]!]] } : null;
}

/** Create / edit an asset and see its relations, maintenance history and documents. */
export function AssetDrawer({
  assetId,
  draft,
  types,
  onDraft,
  onClose,
  onSaved,
  onDeleted,
  onEditGeometry,
}: {
  assetId: string | null;
  draft: Draft | null;
  types: api.AssetType[];
  onDraft: (d: Draft | null) => void;
  onClose: () => void;
  onSaved: (id: string) => void;
  onDeleted: () => void;
  onEditGeometry: (d: Draft) => void;
}) {
  const { t } = useTranslation();
  const detail = useQuery({
    queryKey: ['asset', assetId],
    queryFn: () => fetchAsset(assetId!),
    enabled: !!assetId,
  });
  const a = detail.data?.asset ?? null;
  const [form, setForm] = useState<{
    name: string;
    code: string;
    status: api.Asset['status'];
    installed_on: string;
    notes: string;
    props: Record<string, string | number | boolean | null>;
  } | null>(null);
  const cur =
    form ??
    (a
      ? {
          name: a.name,
          code: a.code ?? '',
          status: a.status,
          installed_on: a.installed_on ?? '',
          notes: a.notes ?? '',
          props: a.properties as Record<string, string | number | boolean | null>,
        }
      : draft
        ? { name: '', code: '', status: 'active' as const, installed_on: '', notes: '', props: {} }
        : null);
  const typeKey = draft?.type_key ?? a?.type_key ?? types[0]?.key ?? '';
  const type = types.find((x) => x.key === typeKey);
  const canManage = draft ? true : (detail.data?.can_manage ?? false);
  const save = useMutation({
    mutationFn: async () => {
      const body: api.AssetSave = {
        client_id: draft?.client_id ?? a!.client_id,
        site_id: a?.site_id ?? null,
        type_key: typeKey,
        name: cur!.name,
        code: cur!.code || null,
        status: cur!.status,
        installed_on: cur!.installed_on || null,
        notes: cur!.notes || null,
        properties: Object.fromEntries(
          Object.entries(cur!.props).filter(([, v]) => v !== '' && v !== null),
        ) as Record<string, string | number | boolean>,
        ...(draft ? { geometry: geometryOf(draft) } : {}),
      };
      return a ? updateAsset(a.id, body) : createAsset(body);
    },
    onSuccess: (r) => {
      setForm(null);
      onSaved(r.id);
    },
  });
  const del = useMutation({ mutationFn: () => deleteAsset(a!.id), onSuccess: onDeleted });
  if (!cur || !type) return null;
  const set = (p: Partial<typeof cur>) => setForm({ ...cur, ...p });
  return (
    <Drawer
      title={a ? a.name : t('assetsPage.new')}
      subtitle={t(`assetTypes.${typeKey}` as 'assetTypes.pole', { defaultValue: type.name_en })}
      onClose={onClose}
      footer={
        canManage ? (
          <div className="toolbar" style={{ width: '100%' }}>
            {a && (
              <button type="button" className="btn btn-danger" onClick={() => del.mutate()}>
                <Trash2 size={14} />
              </button>
            )}
            <span style={{ flex: 1 }} />
            {save.isError && (
              <span className="form-error">
                {t(`errors.${(save.error as Error).message}` as 'errors.generic', {
                  defaultValue: (save.error as Error).message,
                })}
              </span>
            )}
            <button
              type="button"
              className="btn btn-primary"
              disabled={!cur.name.trim() || save.isPending}
              onClick={() => save.mutate()}
            >
              {t('common.save')}
            </button>
          </div>
        ) : undefined
      }
    >
      {draft && (
        <Field label={t('common.type')}>
          <select
            className="input"
            value={draft.type_key}
            onChange={(e) => {
              const nt = types.find((x) => x.key === e.target.value)!;
              onDraft({
                ...draft,
                type_key: nt.key,
                geometryKind: nt.geometry_kind === 'any' ? draft.geometryKind : nt.geometry_kind,
                coords:
                  nt.geometry_kind === 'any' || nt.geometry_kind === draft.geometryKind ? draft.coords : [],
              });
            }}
          >
            {types.map((x) => (
              <option key={x.key} value={x.key}>
                {t(`assetTypes.${x.key}` as 'assetTypes.pole', { defaultValue: x.name_en })}
              </option>
            ))}
          </select>
        </Field>
      )}
      {draft && type.geometry_kind === 'any' && (
        <Field label={t('assetsPage.geometry')}>
          <select
            className="input"
            value={draft.geometryKind}
            onChange={(e) =>
              onDraft({ ...draft, geometryKind: e.target.value as Draft['geometryKind'], coords: [] })
            }
          >
            <option value="point">{t('assetsPage.geom.point')}</option>
            <option value="line">{t('assetsPage.geom.line')}</option>
            <option value="polygon">{t('assetsPage.geom.polygon')}</option>
          </select>
        </Field>
      )}
      {draft && (
        <p className="muted small">
          {t('assetsPage.vertices', { n: draft.coords.length })}{' '}
          {draft.coords.length > 0 && (
            <button type="button" className="btn btn-ghost" onClick={() => onDraft({ ...draft, coords: [] })}>
              {t('assetsPage.clearGeometry')}
            </button>
          )}
        </p>
      )}
      {a && canManage && !draft && (
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() =>
            onEditGeometry({
              id: a.id,
              client_id: a.client_id,
              type_key: a.type_key,
              geometryKind:
                type.geometry_kind === 'any'
                  ? a.geometry?.type === 'Point'
                    ? 'point'
                    : a.geometry?.type === 'LineString'
                      ? 'line'
                      : 'polygon'
                  : type.geometry_kind,
              coords: [],
            })
          }
        >
          {t('assetsPage.redraw')}
        </button>
      )}
      <div className="form-grid">
        <Field label={t('common.name')}>
          <input
            className="input"
            value={cur.name}
            disabled={!canManage}
            onChange={(e) => set({ name: e.target.value })}
            maxLength={200}
          />
        </Field>
        <Field label={t('assetsPage.code')}>
          <input
            className="input"
            value={cur.code}
            disabled={!canManage}
            onChange={(e) => set({ code: e.target.value })}
            maxLength={100}
          />
        </Field>
        <Field label={t('common.status')}>
          <select
            className="input"
            value={cur.status}
            disabled={!canManage}
            onChange={(e) => set({ status: e.target.value as api.Asset['status'] })}
          >
            {(['planned', 'active', 'maintenance', 'faulty', 'retired'] as const).map((s) => (
              <option key={s} value={s}>
                {t(`assetsPage.status.${s}`)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('assetsPage.installedOn')}>
          <input
            className="input"
            type="date"
            value={cur.installed_on}
            disabled={!canManage}
            onChange={(e) => set({ installed_on: e.target.value })}
          />
        </Field>
        {type.properties_schema.map((p) => (
          <Field
            key={p.key}
            label={`${t(`assetsPage.prop.${p.key}` as 'assetsPage.prop.height_m', { defaultValue: p.key })}${p.unit ? ` (${p.unit})` : ''}`}
          >
            {p.type === 'enum' ? (
              <select
                className="input"
                value={String(cur.props[p.key] ?? '')}
                disabled={!canManage}
                onChange={(e) => set({ props: { ...cur.props, [p.key]: e.target.value || null } })}
              >
                <option value="">—</option>
                {(p.options ?? []).map((o) => (
                  <option key={o} value={o}>
                    {t(`assetsPage.option.${o}` as 'assetsPage.option.concrete', { defaultValue: o })}
                  </option>
                ))}
              </select>
            ) : p.type === 'boolean' ? (
              <select
                className="input"
                value={cur.props[p.key] === true ? 'true' : cur.props[p.key] === false ? 'false' : ''}
                disabled={!canManage}
                onChange={(e) =>
                  set({
                    props: {
                      ...cur.props,
                      [p.key]: e.target.value === '' ? null : e.target.value === 'true',
                    },
                  })
                }
              >
                <option value="">—</option>
                <option value="true">{t('assetsPage.yes')}</option>
                <option value="false">{t('assetsPage.no')}</option>
              </select>
            ) : (
              <input
                className="input"
                type={
                  p.type === 'number' || p.type === 'integer' ? 'number' : p.type === 'date' ? 'date' : 'text'
                }
                value={String(cur.props[p.key] ?? '')}
                disabled={!canManage}
                onChange={(e) =>
                  set({
                    props: {
                      ...cur.props,
                      [p.key]:
                        p.type === 'number' || p.type === 'integer'
                          ? e.target.value === ''
                            ? null
                            : Number(e.target.value)
                          : e.target.value,
                    },
                  })
                }
              />
            )}
          </Field>
        ))}
      </div>
      <Field label={t('assetsPage.notes')}>
        <textarea
          className="input textarea"
          rows={2}
          value={cur.notes}
          disabled={!canManage}
          onChange={(e) => set({ notes: e.target.value })}
        />
      </Field>
      {a && detail.data && (
        <SubjectRecords subjectType="asset" subjectId={a.id} clientId={a.client_id} detail={detail.data} />
      )}
    </Drawer>
  );
}

/** Relations, maintenance history and documents of an asset or a device. */
export function SubjectRecords({
  subjectType,
  subjectId,
  clientId,
  detail,
}: {
  subjectType: 'asset' | 'device';
  subjectId: string;
  clientId: string | null;
  detail: api.SubjectDetail;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['asset', subjectId] });
    void qc.invalidateQueries({ queryKey: ['device-records', subjectId] });
  };
  const manage = detail.can_manage;
  const [rel, setRel] = useState<{
    kind: api.Relation['kind'];
    toType: 'asset' | 'device';
    search: string;
    target: string;
  }>({ kind: 'mounted_on', toType: 'asset', search: '', target: '' });
  const candidates = useQuery({
    queryKey: ['relation-candidates', rel.toType, clientId, rel.search],
    queryFn: async () =>
      rel.toType === 'asset'
        ? (await fetchAssets({ client_id: clientId, q: rel.search || undefined })).items.map((x) => ({
            id: x.id,
            name: `${x.name} (${x.type_key})`,
          }))
        : (
            await (
              await getDataSource()
            ).listDevices({ siteId: clientId ?? undefined, search: rel.search, limit: 50 })
          ).items.map((x) => ({ id: x.id, name: x.name })),
    enabled: manage && !!clientId,
  });
  const addRel = useMutation({
    mutationFn: () =>
      addRelation({
        from_type: subjectType,
        from_id: subjectId,
        kind: rel.kind,
        to_type: rel.toType,
        to_id: rel.target,
      }),
    onSuccess: () => (setRel({ ...rel, target: '' }), refresh()),
  });
  const delRel = useMutation({ mutationFn: deleteRelation, onSuccess: refresh });
  const [note, setNote] = useState<{ kind: api.SubjectEventCreate['kind']; title: string; body: string }>({
    kind: 'maintenance',
    title: '',
    body: '',
  });
  const addNote = useMutation({
    mutationFn: () =>
      addSubjectEvent({
        subject_type: subjectType,
        subject_id: subjectId,
        kind: note.kind,
        title: note.title,
        ...(note.body ? { body: note.body } : {}),
      }),
    onSuccess: () => (setNote({ ...note, title: '', body: '' }), refresh()),
  });
  const upload = useMutation({
    mutationFn: (f: File) => uploadDocument(subjectType, subjectId, f),
    onSuccess: refresh,
  });
  const delDoc = useMutation({ mutationFn: deleteDocument, onSuccess: refresh });
  return (
    <>
      <h4>{t('assetsPage.relations')}</h4>
      <ul className="metric-list">
        {detail.relations.map((r) => (
          <li key={r.id}>
            {r.from_name} <strong>{t(`assetsPage.relation.${r.kind}`)}</strong> {r.to_name}
            {manage && (
              <button
                type="button"
                className="btn btn-ghost"
                aria-label={t('common.remove')}
                onClick={() => delRel.mutate(r.id)}
              >
                <Trash2 size={13} />
              </button>
            )}
          </li>
        ))}
        {detail.relations.length === 0 && <li className="muted">{t('assetsPage.noRelations')}</li>}
      </ul>
      {manage && (
        <div className="inline-form">
          <select
            className="input"
            value={rel.kind}
            onChange={(e) => setRel({ ...rel, kind: e.target.value as api.Relation['kind'] })}
            aria-label={t('assetsPage.relations')}
          >
            {RELATIONS.map((k) => (
              <option key={k} value={k}>
                {t(`assetsPage.relation.${k}`)}
              </option>
            ))}
          </select>
          <select
            className="input"
            value={rel.toType}
            onChange={(e) => setRel({ ...rel, toType: e.target.value as 'asset' | 'device', target: '' })}
            aria-label={t('common.type')}
          >
            <option value="asset">{t('assetsPage.anAsset')}</option>
            <option value="device">{t('assetsPage.aDevice')}</option>
          </select>
          <input
            className="input"
            value={rel.search}
            onChange={(e) => setRel({ ...rel, search: e.target.value })}
            placeholder={t('common.search')}
            aria-label={t('common.search')}
          />
          <select
            className="input"
            value={rel.target}
            onChange={(e) => setRel({ ...rel, target: e.target.value })}
            aria-label={t('assetsPage.target')}
          >
            <option value="">—</option>
            {(candidates.data ?? [])
              .filter((c) => c.id !== subjectId)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </select>
          <button
            type="button"
            className="btn"
            disabled={!rel.target || addRel.isPending}
            onClick={() => addRel.mutate()}
          >
            {t('assetsPage.addRelation')}
          </button>
        </div>
      )}
      <h4>{t('assetsPage.history')}</h4>
      {manage && (
        <div className="inline-form" style={{ marginBottom: 8 }}>
          <select
            className="input"
            value={note.kind}
            onChange={(e) => setNote({ ...note, kind: e.target.value as api.SubjectEventCreate['kind'] })}
            aria-label={t('common.type')}
          >
            {MAINT.map((k) => (
              <option key={k} value={k}>
                {t(`assetsPage.event.${k}`)}
              </option>
            ))}
          </select>
          <input
            className="input"
            value={note.title}
            onChange={(e) => setNote({ ...note, title: e.target.value })}
            placeholder={t('assetsPage.noteTitle')}
            maxLength={200}
            aria-label={t('assetsPage.noteTitle')}
          />
          <input
            className="input"
            value={note.body}
            onChange={(e) => setNote({ ...note, body: e.target.value })}
            placeholder={t('assetsPage.noteBody')}
            aria-label={t('assetsPage.noteBody')}
          />
          <button
            type="button"
            className="btn"
            disabled={!note.title.trim() || addNote.isPending}
            onClick={() => addNote.mutate()}
          >
            {t('assetsPage.addNote')}
          </button>
        </div>
      )}
      <div className="timeline">
        {detail.events.map((e) => (
          <div key={e.id} className="timeline-item">
            <strong>
              {t(`assetsPage.event.${e.kind}` as 'assetsPage.event.note', { defaultValue: e.kind })}
            </strong>
            {e.title && ` · ${e.title}`}
            <span className="cell-sub">
              {formatDateTime(e.at)} · {e.author ?? '—'}
            </span>
            {e.body && <div className="small">{e.body}</div>}
          </div>
        ))}
        {detail.events.length === 0 && <span className="muted">{t('assetsPage.noHistory')}</span>}
      </div>
      <h4>{t('assetsPage.documents')}</h4>
      <ul className="metric-list">
        {detail.documents.map((d) => (
          <li key={d.id}>
            <a href={documentUrl(d.id)} download>
              <FileDown size={13} /> {d.filename}
            </a>{' '}
            <span className="muted small">
              {formatBytes(d.size_bytes)} · {formatDateTime(d.uploaded_at)} · {d.uploaded_by ?? '—'}
            </span>
            {manage && (
              <button
                type="button"
                className="btn btn-ghost"
                aria-label={t('common.delete')}
                onClick={() => delDoc.mutate(d.id)}
              >
                <Trash2 size={13} />
              </button>
            )}
          </li>
        ))}
      </ul>
      {manage && (
        <label className="btn">
          <Upload size={14} /> {upload.isPending ? t('assetsPage.uploading') : t('assetsPage.upload')}
          <input
            type="file"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) upload.mutate(f);
              e.target.value = '';
            }}
          />
        </label>
      )}
      {upload.isError && <p className="form-error">{t('assetsPage.uploadFailed')}</p>}
    </>
  );
}
